import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Plus, Trash2, Heart, X, Search, Loader2, ShoppingCart, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { client, ximilarFetch } from '../api/client';
import { supabase } from '../lib/supabase';

export const WishlistDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [wishlistName, setWishlistName] = useState('Wishlist');
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Add item modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [addingCardId, setAddingCardId] = useState<string | null>(null);

  // Delete confirm
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    fetchWishlistItems();
  }, [id]);

  const fetchWishlistItems = async () => {
    if (!id) return;
    setLoading(true);
    try {
      // Fetch wishlist info
      const { data: wishlist } = await supabase
        .from('wishlists')
        .select('name')
        .eq('id', id)
        .single();

      if (wishlist) setWishlistName(wishlist.name);

      // Fetch wishlist items
      const { data: wishlistItems } = await supabase
        .from('wishlist_items')
        .select('*')
        .eq('wishlist_id', id)
        .order('created_at', { ascending: false });

      if (wishlistItems) {
        setItems(wishlistItems.map(item => ({
          id: item.id,
          cardName: item.card_name,
          imageUrl: item.image_url,
          catalogCardId: item.card_id
        })));
      }
    } catch (e) {
      console.error('Failed to fetch wishlist items', e);
    } finally {
      setLoading(false);
    }
  };

  const searchCatalog = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await ximilarFetch(`/api/catalog/search?q=${encodeURIComponent(searchQuery)}`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.data || []);
      }
    } catch (e) {
      console.error('Catalog search failed', e);
    } finally {
      setSearching(false);
    }
  };

  const addItemToWishlist = async (card: any) => {
    if (!id) return;
    setAddingCardId(card.id);
    try {
      // For proxied images, strictly we should use the edge function URL or similar,
      // but simpler to just store what we have or a placeholder if it's dynamic.
      // Since backendUrl is gone, we rely on the card's existing URL or leave it null.
      const imageUrl = card.image_url || card.imageUrl;

      const { error } = await supabase.from('wishlist_items').insert({
        wishlist_id: id,
        card_id: card.id || null, // Ximilar ID if available
        card_name: card.name,
        image_url: imageUrl
      });

      if (error) throw error;

      setAddModalOpen(false);
      fetchWishlistItems();
    } catch (e) {
      console.error('Failed to add item', e);
    } finally {
      setAddingCardId(null);
    }
  };

  const removeItem = async (itemId: number) => {
    if (!id) return;
    setDeletingId(itemId);
    try {
      const { error } = await supabase
        .from('wishlist_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;

      setItems(prev => prev.filter(i => i.id !== itemId));
    } catch (e) {
      console.error('Failed to remove item', e);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Background atmosphere */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-radial from-danger/5 to-transparent rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
      </div>

      <div className="relative z-10 p-5 pt-16 pb-32 space-y-6">
        {/* Header */}
        <header className="sticky top-14 z-20 bg-obsidian/90 backdrop-blur-xl py-3 -mx-5 px-5 border-b border-white/[0.04]">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate('/collection')}
              className="p-2 card-premium rounded-xl hover:bg-white/5 transition-colors"
              aria-label="Back to Collection"
            >
              <ChevronLeft className="w-5 h-5 text-cream-200/60" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-display font-semibold text-cream-50">{wishlistName}</h1>
              <p className="text-cream-200/40 text-xs mt-0.5">{items.length} items</p>
            </div>
            <button
              onClick={() => { setAddModalOpen(true); setSearchQuery(''); setSearchResults([]); }}
              className="p-2.5 bg-danger/20 rounded-xl hover:bg-danger/30 transition-colors border border-danger/30"
              aria-label="Add Item to Wishlist"
            >
              <Plus className="w-4 h-4 text-danger" />
            </button>
          </div>
        </header>

        {/* Loading */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="flex gap-1.5">
              {[0, 1, 2].map(i => (
                <div key={i} className="w-2 h-8 bg-danger/30 rounded-full animate-pulse" style={{ animationDelay: `${i * 150}ms` }} />
              ))}
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-danger/10 rounded-3xl flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-danger/50" />
            </div>
            <h3 className="text-cream-100 font-semibold mb-2">Wishlist is empty</h3>
            <p className="text-cream-200/40 text-sm mb-6">Search the catalog to add cards you want</p>
            <button
              onClick={() => { setAddModalOpen(true); setSearchQuery(''); setSearchResults([]); }}
              className="px-6 py-3 bg-danger/20 text-danger rounded-xl font-semibold border border-danger/30 hover:bg-danger/30 transition-colors"
            >
              Search Cards
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item: any, i: number) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="card-premium p-3 flex items-center gap-4 group"
              >
                <div className="w-16 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-obsidian border border-white/5">
                  <img
                    src={item.imageUrl && item.imageUrl.startsWith('http') ? item.imageUrl : 'https://placehold.co/80x100/1C1C1F/666?text=Card'}
                    alt={item.cardName || 'Wishlist item'}
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).src = 'https://placehold.co/80x100/1C1C1F/666?text=Card'; }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-cream-100 truncate">{item.cardName || 'Unknown Card'}</h3>
                  {item.catalogCardId && (
                    <p className="text-cream-200/30 text-xs truncate mt-0.5">ID: {item.catalogCardId}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Search on eBay */}
                  {item.cardName && (
                    <button
                      onClick={() => {
                        const query = encodeURIComponent(item.cardName.trim());
                        window.open(`https://www.ebay.com/sch/i.html?_nkw=${query}&_sacat=212`, '_blank');
                      }}
                      className="p-2 bg-white/[0.04] rounded-lg hover:bg-white/[0.08] text-cream-200/40 hover:text-cream-100 transition-colors"
                      title="Search on eBay"
                      aria-label="Search on eBay"
                    >
                      <ExternalLink size={14} />
                    </button>
                  )}
                  {/* Browse in catalog */}
                  {item.cardName && (
                    <button
                      onClick={() => navigate(`/catalog?q=${encodeURIComponent(item.cardName)}`)}
                      className="p-2 bg-white/[0.04] rounded-lg hover:bg-white/[0.08] text-cream-200/40 hover:text-cream-100 transition-colors"
                      title="Find in catalog"
                      aria-label="Find in catalog"
                    >
                      <ShoppingCart size={14} />
                    </button>
                  )}
                  {/* Remove */}
                  <button
                    onClick={() => removeItem(item.id)}
                    disabled={deletingId === item.id}
                    className="p-2 bg-white/[0.04] rounded-lg hover:bg-danger/20 text-cream-200/40 hover:text-danger transition-colors"
                    aria-label="Remove item"
                    title="Remove item"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Add Item Modal - Search from Catalog */}
      <AnimatePresence>
        {addModalOpen && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/80 backdrop-blur-sm">
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="bg-carbon border-t border-white/[0.06] rounded-t-3xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
            >
              <div className="p-6 pb-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-display font-semibold text-cream-50">Add to Wishlist</h3>
                  <button onClick={() => setAddModalOpen(false)} className="p-1.5 rounded-lg hover:bg-white/5 text-cream-200/50 hover:text-cream-100 transition-colors">
                    <X size={18} />
                  </button>
                </div>
                <form onSubmit={searchCatalog} className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cream-200/40" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search the card catalog..."
                    className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl py-3 pl-10 pr-20 text-sm text-cream-100 placeholder-cream-200/30 focus:outline-none focus:border-danger/50 transition-all"
                  />
                  <button
                    type="submit"
                    disabled={searching}
                    className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-danger/20 text-danger rounded-lg text-xs font-semibold border border-danger/30 disabled:opacity-50"
                  >
                    {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Search'}
                  </button>
                </form>
              </div>

              <div className="flex-1 overflow-y-auto p-6 pt-2 space-y-2">
                {searching ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 text-danger animate-spin" />
                  </div>
                ) : searchResults.length === 0 ? (
                  <p className="text-cream-200/40 text-sm text-center py-8">
                    {searchQuery ? 'No cards found. Try a different search.' : 'Search the catalog to find cards to add.'}
                  </p>
                ) : (
                  searchResults.map((card: any) => (
                    <button
                      key={card.id}
                      onClick={() => addItemToWishlist(card)}
                      disabled={addingCardId === card.id}
                      className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors border bg-white/[0.02] border-white/[0.04] hover:bg-white/[0.05]"
                    >
                      <div className="w-12 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-obsidian">
                        <img
                          src={card.image_url || card.imageUrl || 'https://placehold.co/60x80/1C1C1F/666?text=Card'}
                          alt={card.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { (e.target as HTMLImageElement).src = 'https://placehold.co/60x80/1C1C1F/666?text=Card'; }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-cream-100 text-sm font-medium truncate">{card.name}</p>
                        <p className="text-cream-200/40 text-xs truncate">{card.setName || card.set_name || ''}</p>
                        {(card.prices?.raw || card.price) && (
                          <p className="text-success text-xs font-semibold mt-0.5">
                            {card.prices?.raw ? `$${card.prices.raw}` : card.price}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {addingCardId === card.id ? (
                          <Loader2 className="w-4 h-4 text-danger animate-spin" />
                        ) : (
                          <Heart className="w-4 h-4 text-danger" />
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
